import { useState, useMemo } from 'react'
import { Calculator, ChevronDown, ChevronUp } from 'lucide-react'
import { formatPriceWithCents } from '@/utils/format'

interface FinanceCalculatorProps {
  price: number
  /** Dealer-customisable default interest rate */
  defaultRate?: number
  /** Dealer branding (fallback when no provider set) */
  dealerName?: string
  dealerLogo?: string
  /** Finance provider details */
  providerName?: string
  providerLogo?: string
  providerUrl?: string
  contactEmail?: string
  contactPhone?: string
  minAmount?: number
  termsUrl?: string
}

/**
 * Finance calculator for vehicle listings.
 * Shows estimated monthly repayments based on price, deposit, term, and interest rate.
 * Collapsible by default to keep the listing page clean.
 */
export function FinanceCalculator({
  price,
  defaultRate = 9.95,
  dealerName,
  dealerLogo,
  providerName,
  providerLogo,
  providerUrl,
  contactEmail,
  contactPhone,
  minAmount,
  termsUrl,
}: FinanceCalculatorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [deposit, setDeposit] = useState(Math.round(price * 0.1)) // Default 10% deposit
  const [termMonths, setTermMonths] = useState(60) // Default 5 years
  const [rate, setRate] = useState(defaultRate)

  const loanAmount = Math.max(0, price - deposit)

  const monthlyPayment = useMemo(() => {
    if (loanAmount <= 0 || rate <= 0 || termMonths <= 0) return 0
    const monthlyRate = rate / 100 / 12
    // Standard amortisation formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const numerator = monthlyRate * Math.pow(1 + monthlyRate, termMonths)
    const denominator = Math.pow(1 + monthlyRate, termMonths) - 1
    return loanAmount * (numerator / denominator)
  }, [loanAmount, rate, termMonths])

  const totalRepayment = monthlyPayment * termMonths
  const totalInterest = totalRepayment - loanAmount

  return (
    <div className="card overflow-hidden">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-primary-600" />
          <span className="font-medium text-sm">Finance Calculator</span>
          {!isOpen && monthlyPayment > 0 && (
            <span className="text-xs text-slate-500 ml-1">
              est. {formatPriceWithCents(monthlyPayment)}/mo
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {/* Calculator body */}
      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t border-slate-100">
          {/* Finance provider branding */}
          {(providerName || dealerName) && (
            <div className="flex items-center gap-2 pt-3">
              {(providerLogo || dealerLogo) && (
                <img
                  src={providerLogo || dealerLogo}
                  alt={providerName || dealerName || ''}
                  className="w-6 h-6 rounded object-cover"
                />
              )}
              {providerName && providerUrl ? (
                <a href={providerUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:text-primary-700">
                  Finance by {providerName}
                </a>
              ) : (
                <span className="text-xs text-slate-500">
                  Finance {providerName ? `by ${providerName}` : dealerName ? `estimate by ${dealerName}` : 'calculator'}
                </span>
              )}
            </div>
          )}

          {/* Minimum amount note */}
          {minAmount && price < minAmount && (
            <div className="pt-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1.5">
              Minimum finance amount: ${minAmount.toLocaleString()}
            </div>
          )}

          {/* Monthly payment display */}
          <div className="text-center py-3">
            <p className="text-3xl font-bold text-primary-600">
              {formatPriceWithCents(monthlyPayment)}
            </p>
            <p className="text-sm text-slate-500">per month</p>
          </div>

          {/* Inputs */}
          <div className="space-y-3">
            {/* Vehicle price (read-only) */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Vehicle Price</span>
              <span className="font-medium">{formatPriceWithCents(price)}</span>
            </div>

            {/* Deposit */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-slate-600">Deposit</label>
                <span className="text-sm font-medium">{formatPriceWithCents(deposit)}</span>
              </div>
              <input
                type="range"
                min={0}
                max={Math.round(price * 0.9)}
                step={100}
                value={deposit}
                onChange={(e) => setDeposit(Number(e.target.value))}
                className="w-full accent-primary-500"
              />
              <div className="flex justify-between text-xs text-slate-400">
                <span>$0</span>
                <span>{Math.round((deposit / price) * 100)}%</span>
                <span>{formatPriceWithCents(price * 0.9)}</span>
              </div>
            </div>

            {/* Loan term */}
            <div>
              <label className="text-sm text-slate-600 block mb-1">Loan Term</label>
              <div className="grid grid-cols-4 gap-1.5">
                {[24, 36, 48, 60].map((months) => (
                  <button
                    key={months}
                    type="button"
                    onClick={() => setTermMonths(months)}
                    className={`py-1.5 rounded text-xs font-medium transition-colors ${
                      termMonths === months
                        ? 'bg-primary-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {months / 12}yr
                  </button>
                ))}
              </div>
            </div>

            {/* Interest rate */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-slate-600">Interest Rate</label>
                <span className="text-sm font-medium">{rate.toFixed(2)}%</span>
              </div>
              <input
                type="range"
                min={1}
                max={25}
                step={0.25}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                className="w-full accent-primary-500"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Loan Amount</span>
              <span>{formatPriceWithCents(loanAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Total Interest</span>
              <span>{formatPriceWithCents(totalInterest)}</span>
            </div>
            <div className="flex justify-between font-medium pt-1.5 border-t border-slate-200">
              <span>Total Repayment</span>
              <span>{formatPriceWithCents(totalRepayment)}</span>
            </div>
          </div>

          {/* Provider footer */}
          {(contactEmail || contactPhone || termsUrl) && (
            <div className="text-xs text-slate-500 space-y-1 pt-1">
              {(contactEmail || contactPhone) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  {contactEmail && (
                    <a href={`mailto:${contactEmail}`} className="text-primary-600 hover:text-primary-700">{contactEmail}</a>
                  )}
                  {contactPhone && (
                    <a href={`tel:${contactPhone}`} className="text-primary-600 hover:text-primary-700">{contactPhone}</a>
                  )}
                </div>
              )}
              {termsUrl && (
                <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                  Terms & conditions apply
                </a>
              )}
            </div>
          )}

          {/* Disclaimer */}
          <p className="text-[10px] text-slate-400 leading-tight">
            This calculator provides estimates only and does not constitute a finance offer.
            Actual rates and terms depend on your individual circumstances and the finance provider.
            {!contactEmail && !contactPhone && ' Contact a finance provider for a personalised quote.'}
          </p>
        </div>
      )}
    </div>
  )
}
